import Phaser from 'phaser';
import { ASSET_H, ASSET_W, DEFAULT_PANEL } from '../constants';
import { applySceneRenderBudget } from '../render-budget';
import { scrollForPanel } from '../layout';
import { gameState } from '../state';
import { createPanelCamera } from '../systems/panel-camera';
import { createPanelInput } from '../systems/panel-input';
import { onGame, offGame, GameEvents, emitGame } from '../events';
import { createDefaultWorldModels } from '../components/world-model';
import { createStageComponent } from '../components/stage';
import { createGeodeComponent } from '../components/geode';
import { createWeaponComponent } from '../components/weapon';
import { createBoogasComponent } from '../components/boogas';
import type { WorldModels } from '../components/types';

export class WorldScene extends Phaser.Scene {
  private world!: WorldModels;
  private stage!: ReturnType<typeof createStageComponent>;
  private panelCamera!: ReturnType<typeof createPanelCamera>;
  private geode!: ReturnType<typeof createGeodeComponent>;
  private weapon!: ReturnType<typeof createWeaponComponent>;
  private boogas!: ReturnType<typeof createBoogasComponent>;

  constructor() {
    super({ key: 'WorldScene' });
  }

  create() {
    applySceneRenderBudget(this);

    gameState.currentPanel = DEFAULT_PANEL;
    this.world = createDefaultWorldModels();

    this.cameras.main.setBounds(0, 0, ASSET_W, ASSET_H);
    this.cameras.main.setScroll(scrollForPanel(DEFAULT_PANEL), 0);

    this.stage = createStageComponent(this, this.world.stage);
    this.panelCamera = createPanelCamera(this);
    this.geode = createGeodeComponent(this, this.world.geode);
    this.weapon = createWeaponComponent(this, this.world.weapon);
    this.boogas = createBoogasComponent(this, this.world.boogas);
    void this.boogas.init();

    this.syncAll(this.cameras.main.scrollX);

    this.panelCamera.setOnScroll((scrollX) => {
      this.syncAll(scrollX);
    });

    this.onPanelChanged = () => {
      this.syncAll(this.cameras.main.scrollX);
    };
    this.onPanelRequest = (index) => {
      const panel = index as 0 | 1;
      if (panel !== 0 && panel !== 1) return;
      if (panel === gameState.currentPanel) return;
      void this.panelCamera.panToPanel(panel).then(() => {
        this.panelCamera.setPanelInstant(panel);
        gameState.currentPanel = panel;
        emitGame(GameEvents.PANEL_CHANGED, panel);
      });
    };
    onGame(GameEvents.PANEL_CHANGED, this.onPanelChanged, this);
    onGame(GameEvents.PANEL_REQUEST, this.onPanelRequest, this);
    onGame(GameEvents.PANEL_REQUEST, this.onPanelRequest, this);

    createPanelInput(
      this,
      this.panelCamera,
      (pointer) => this.geode.onTap(pointer),
      (pointer) => this.geode.pointerAtDrop(pointer),
      (pointer) => this.geode.tryPickup(pointer),
      () => this.boogas.onTigerTap(),
      (pointer) => this.boogas.tigerHitTest(pointer),
    );
  }

  private onPanelChanged!: () => void;
  private onPanelRequest!: (...args: unknown[]) => void;

  applyWorldModels(next: WorldModels) {
    this.world = next;
    this.stage.setModel(next.stage);
    this.geode.setModel(next.geode);
    this.weapon.setModel(next.weapon);
    this.boogas.setModel(next.boogas);
  }

  private syncAll(scrollX: number) {
    this.geode.syncFromScroll(scrollX);
    this.weapon.syncFromScroll(scrollX);
    this.boogas.syncFromScroll(scrollX);
  }

  shutdown() {
    offGame(GameEvents.PANEL_CHANGED, this.onPanelChanged, this);
    offGame(GameEvents.PANEL_REQUEST, this.onPanelRequest, this);
    this.geode?.destroy();
    this.weapon?.destroy();
    this.boogas?.destroy();
    this.stage?.destroy();
  }

  update(_time: number, delta: number) {
    this.stage.update(delta);
    this.geode.updateParticles(delta);
  }
}
